import { clone } from "lodash";

import client, { configuration } from "../api/client/SdsWebApiClient";
import localConfig from "../localConfig.json";
import { Convert, IntroConfig, Preset } from "../models/IntroConfig";
import { StringPair } from "../api/generated";

export const getConfigFile = async (path: string | undefined | null): Promise<IntroConfig> => {
    if (!path) Convert.toIntroConfig(JSON.stringify(localConfig));
    const response = await fetch(path!, { cache: "no-cache" });
    const config = await response.json();

    return Convert.toIntroConfig(JSON.stringify(config || localConfig));
};

export const getStylingFile = async (path: string | undefined | null): Promise<any> => {
    if (!path) return {};
    const response = await fetch(path!, { cache: "no-cache" });
    return response.json();
};

export const getApplicationSettings = async () => {
    const settings = await client.getClientSettingsGet();
    if (!settings || !settings.dict) return new Array<StringPair>();
    return settings.dict;
};

export const getApplicationSetting = (settings: StringPair[], key: string): string => {
    return settings.find(x => x.key === key)?.value || "";
};

export const getFrontendSettings = async () => {
    const response = await fetch(`${configuration.basePath}/frontend_settings.json`, { cache: "no-cache" });
    const settings = await response.json();
    return settings;
};

export const getAppVersion = async () => {
    const response = await fetch("./appversion.txt", { cache: "no-cache" });
    if (response.ok) {
        const appVersion = (await response.text()).trim();
        if (/^\d+(\.\d+)*$/.test(appVersion)) {
            return appVersion;
        }
    }
    return "Development";
};

export const getLanguage = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("lang")?.toUpperCase() || (await client.getDefaultLanguageGet());
};

export const getAccountId = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("account") || (await client.getDefaultSdsAccountGet());
};

export const getImageUrlForConfig = async (
    config: string,
    market: string | undefined,
    serieName: string,
    composedUrl: string,
    width: number,
    height: number,
    accountId: string | null
) => {
    const imagePath = await client.getConfigImageVer2Get(
        config,
        "default",
        market,
        width,
        height,
        0,
        1,
        0,
        0,
        "",
        "",
        "",
        serieName,
        1,
        "jpg",
        accountId || undefined,
        false
    );

    return composedUrl + JSON.parse(imagePath);
};

export const filterSetsRecursive = (sets: Preset[], setFilter: string[]) => {
    const filteredSets: Preset[] = [];
    sets.forEach(set => {
        let { config, serie } = set;
        const filteredChildSets = set.sets && set.sets.length > 0 ? filterSetsRecursive(set.sets, setFilter) : [];

        const model = getModelFromConfig(config);
        if (!setFilter.some(s => equalsCaseInsensitive(s, model))) {
            if (filteredChildSets.length === 0) {
                // Model not found in filter and no active child sets, skip.
                return; // continue
            }

            // Use the config from the first available child set instead of the excluded set.
            config = filteredChildSets[0].config;
            serie = filteredChildSets[0].serie;
        }

        const filteredSet = clone(set);
        if (filteredChildSets.length > 0) {
            filteredSet.sets = filteredChildSets;
        } else if (filteredSet.sets) {
            delete filteredSet.sets;
        }
        filteredSet.config = config;
        filteredSet.serie = serie;
        filteredSets.push(filteredSet);
    });

    // Order sets by the same order as in the filter
    const orderedSets: Preset[] = [];
    setFilter.forEach(filterModel => {
        filteredSets.forEach(set => {
            const model = getModelFromConfig(set.config);
            if (equalsCaseInsensitive(model, filterModel)) {
                orderedSets.push(set);
            }
        });
    });
    return orderedSets;
};

const equalsCaseInsensitive = (a: string, b: string) => {
    return a.localeCompare(b, "en", { sensitivity: "base" }) === 0;
};

export const getModelFromConfig = (config: string) => {
    // Assuming the config is always in either classic or swift "default" encoding,
    // with the model at the start of the string.
    const firstDot = config.indexOf(".");
    const firstSemicolon = config.indexOf(";");
    let modelEndIndex;
    if (firstDot !== -1 && firstSemicolon !== -1) {
        modelEndIndex = Math.min(firstDot, firstSemicolon);
    } else if (firstDot !== -1) {
        modelEndIndex = firstDot;
    } else {
        modelEndIndex = firstSemicolon;
    }
    return modelEndIndex === -1 ? config : config.substring(0, modelEndIndex);
};
